import { Popover, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { EditableCell, EditableCellProps, TableColumns } from '@ohpen/enso-ui';
import { TableHeaderTitle } from './TableHeaderTitle';
import { GetFormattedColumnsProps, GetOnCellProps, WithRowNumber } from '../interfaces';
import { ChangeRequestRowFeedback, FieldType } from '@pmp/api';
import { disableEditAllKeys } from '../utils';
import { t } from 'i18next';
import styles from '../styles.module.css';

export const getComponents = <EntityChangeDataRow,>(virtual: boolean) => {
  const Wrapper = virtual ? 'div' : 'td';

  return {
    body: {
      row: virtual ? (props: any) => <div {...props} /> : undefined,
      cell: ({
        children,
        column,
        record,
        originalRecord,
        valueList,
        title,
        editable,
        editableCells,
        editAll,
        showIds,
        mode,
        goToNext,
        goToPrev,
        setActiveCell,
        getNumberOfRows,
        handleSave,
        ...props
      }: EditableCellProps<EntityChangeDataRow>) => {
        return !record ? (
          <Wrapper {...props}>{children}</Wrapper>
        ) : (
          <Wrapper {...props}>
            <EditableCell
              column={column}
              record={record}
              originalRecord={originalRecord}
              valueList={valueList}
              title={title}
              editable={editable}
              editableCells={editableCells}
              editAll={editAll}
              showIds={showIds}
              mode={mode}
              goToNext={goToNext}
              goToPrev={goToPrev}
              setActiveCell={setActiveCell}
              getNumberOfRows={getNumberOfRows}
              handleSave={handleSave}
            >
              {children}
            </EditableCell>
          </Wrapper>
        );
      },
    },
  };
};

const getOnCell =
  <EntityData, EntityCrudData, EntityChangeDataRow>({
    column,
    editableCells,
    editAll,
    getNumberOfRows,
    goToNext,
    goToPrev,
    handleSave,
    isClone,
    isCreate,
    isEdit,
    isUpdateChange,
    isUpdateImportChange,
    originalDataObject,
    setActiveCell,
    showIds,
    title,
    valueList,
  }: GetOnCellProps<EntityData, EntityCrudData, EntityChangeDataRow>) =>
  (record: WithRowNumber<EntityCrudData>) => {
    const editable: boolean =
      (isClone && column.key !== 'originalCode' && column.key !== 'replacesCode') ||
      (isEdit && editableCells?.[record.rowNumber]?.[column.dataIndex as string]) ||
      (isCreate && column.key !== 'actions') ||
      (isUpdateChange && editableCells?.[record.rowNumber]?.[column.dataIndex as string]) ||
      (isUpdateImportChange && editableCells?.[record.rowNumber]?.[column.dataIndex as string]);
    const originalRecord = originalDataObject?.[record.rowNumber];

    return {
      key: record.rowNumber,
      column,
      record,
      originalRecord,
      valueList,
      title,
      editable,
      editableCells,
      editAll,
      showIds,
      mode: 'crud',
      goToNext,
      goToPrev,
      setActiveCell,
      getNumberOfRows,
      handleSave,
    };
  };

export const getFormattedColumns = <EntityData, EntityCrudData, EntityChangeDataRow>({
  allColumns,
  columnsEditAll,
  columnsErrorsCount,
  editableCells,
  getNumberOfRows,
  goToNext,
  goToPrev,
  handleChangeEditAll,
  handleSave,
  isClone,
  isCreate,
  isEdit,
  isUpdateChange,
  isUpdateImportChange,
  originalDataObject,
  setActiveCell,
  showIds,
  validationFeedback,
  valueLists,
}: GetFormattedColumnsProps<EntityData, EntityCrudData, EntityChangeDataRow>) => {
  const cols = (
    isClone
      ? [{ ...allColumns[0], title: 'pmp.products.labels.originalCode', key: 'originalCode', dataIndex: 'originalCode' }, ...allColumns]
      : allColumns
  ).map(column => {
    const editAll = columnsEditAll[column.key as string] ?? false;
    const valueList = valueLists[column.options?.format?.type === 'boolean' ? FieldType.YES_NO : (column.options?.listName ?? '')];
    const title = String(column.title);

    function getType() {
      if (column.options?.format?.type === 'valueList') {
        return showIds ? 'valueListKey' : 'valueList';
      }
      if (column.options?.format?.type === 'multiValueList') {
        return showIds ? 'multiValueListKey' : 'multiValueList';
      }
      if (column.options?.format?.type === 'boolean') {
        return showIds ? 'booleanKey' : 'boolean';
      }
      return column.options?.format?.type;
    }

    return {
      ...column,
      options: {
        ...column.options,
        filter: undefined,
        sorter: undefined,
        hide: false,
        format: {
          ...(column.options?.format ?? {}),
          type: getType(),
        },
      },
      title: () => (
        <TableHeaderTitle
          columnKey={String(column.key)}
          title={column.options?.isCustom ? title : t(title)}
          editAll={editAll}
          disableEditAllKeys={disableEditAllKeys}
          columnsErrorsCount={columnsErrorsCount}
          validationFeedback={validationFeedback}
          handleChangeEditAll={handleChangeEditAll}
        />
      ),
      width: column.key === 'actions' ? 70 : 220,
      onCell: getOnCell({
        column,
        editableCells,
        editAll,
        getNumberOfRows,
        goToNext,
        goToPrev,
        handleSave,
        isClone,
        isCreate,
        isEdit,
        isUpdateChange,
        isUpdateImportChange,
        originalDataObject,
        setActiveCell,
        showIds,
        title,
        valueList,
      }),
    };
  }) as TableColumns<EntityCrudData>;

  const hasRowErrors =
    validationFeedback != null && Object.values(validationFeedback as Record<string, ChangeRequestRowFeedback>).some(feedback => !!feedback?.errors?.length);

  if (!hasRowErrors) return cols;

  const errorColumn = {
    key: '__row_errors__',
    fixed: 'left',
    width: 40,
    align: 'center',
    render: (_: unknown, record: EntityCrudData & { rowFeedback?: ChangeRequestRowFeedback }) => {
      const errors: string[] = record.rowFeedback?.errors ?? [];
      if (!errors?.length) return null;

      const popoverContent = (
        <div className={styles.cellPopoverContent}>
          <div className={`${styles.cellPopoverContainer} ${styles.cellPopoverContainerError}`}>
            <Typography.Title level={5}>{t('global.labels.rowErrors')}:</Typography.Title>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      );
      return (
        <Popover placement="right" content={popoverContent}>
          <ExclamationCircleOutlined style={{ color: 'var(--enso-icon-danger-color)', fontSize: 16 }} />
        </Popover>
      );
    },
  };

  return [errorColumn, ...cols] as TableColumns<EntityCrudData>;
};
